import { UI, HStack, HStack as NBHStack, VStack as NBVStack } from '@genial/components';
import { Spacing, Typography } from '@genial/design-tokens';
import React, { useEffect } from 'react';

import { useScrollCloseToBottomContext } from '../../../../../contexts/ScrollCloseToBottom';
import { NotFoundTable, messageError } from '../DynamicProductTable/constants';
import { IndicatorIcon } from '../IndicatorIcon';

interface ProductFieldProps<T> {
  key: keyof T;
  label: string;
  indicatorKey?: keyof T;
}

export interface ProductTableMobileProps<T> {
  data: T[];
  isLoading: boolean;
  isLoadingMore: boolean;
  fields: ProductFieldProps<T>[];
  handleEndReached: () => void;
  ItemBottomComponent?: React.FC<{ item: T }>;
}

export const ProductTableMobile = <T,>({
  data,
  fields,
  isLoading,
  isLoadingMore,
  ItemBottomComponent,
  handleEndReached,
}: ProductTableMobileProps<T>) => {
  const { isCloseToBottom } = useScrollCloseToBottomContext();

  const renderItem = (label: string, value: string | number = messageError, indicator?: number) => {
    const valueComponent = (
      <UI.Text
        isLoading={isLoading}
        fontSize={Typography['fontsize.xs']}
        variant="semibold"
        textAlign="right">
        {`${value}`}
      </UI.Text>
    );

    return (
      <NBHStack
        justifyContent="space-between"
        alignContent="center"
        space={Spacing['inline.quarck']}>
        <UI.Text fontSize={Typography['fontsize.xs']} color="neutral.dark">
          {label}
        </UI.Text>

        {indicator !== undefined ? (
          <HStack>
            <IndicatorIcon percentage={indicator} />
            {valueComponent}
          </HStack>
        ) : (
          valueComponent
        )}
      </NBHStack>
    );
  };

  const renderLoadingItem = (label: string) => (
    <NBHStack justifyContent="space-between" alignContent="center" space={Spacing['inline.quarck']}>
      <UI.Text fontSize={Typography['fontsize.xs']} color="neutral.dark">
        {label}
      </UI.Text>
      <UI.Text isLoading fontSize={Typography['fontsize.xs']} />
    </NBHStack>
  );

  const renderProductItem = (item: T, index: number) => (
    <UI.Shadow testId="shadow" level={1} borderRadius="8px" key={index.toString()}>
      <NBVStack p={Spacing['inset.xs']} space={Spacing['inset.xs']}>
        {fields.map((field: ProductFieldProps<T>) =>
          renderItem(
            field.label,
            item[field.key] as string,
            field.indicatorKey ? (item[field.indicatorKey] as number) : undefined
          )
        )}
      </NBVStack>
      {ItemBottomComponent && <ItemBottomComponent item={item} />}
    </UI.Shadow>
  );

  const renderProductItemLoading = (index: number) => (
    <UI.Shadow testId="shadow" level={1} borderRadius="8px" key={index.toString()}>
      <NBVStack p={Spacing['inset.xs']} space={Spacing['inset.xs']}>
        {fields.map((field: ProductFieldProps<T>) => renderLoadingItem(field.label))}
      </NBVStack>
    </UI.Shadow>
  );

  useEffect(() => {
    if (isCloseToBottom && !isLoading) {
      handleEndReached();
    }
  }, [isCloseToBottom, isLoading, handleEndReached]);

  if (isLoading) {
    return (
      <NBVStack space={Spacing['stack.xxxs']}>
        {Array.from({ length: 3 }).map((_, index) => renderProductItemLoading(index))}
      </NBVStack>
    );
  }

  if (data?.length === 0 && !isLoading) {
    return <NotFoundTable />;
  }

  return (
    <NBVStack space={Spacing['stack.xxxs']} pb={Spacing['stack.xxxs']}>
      {data.map((item, index) => renderProductItem(item, index))}
      {!!isLoadingMore && renderProductItemLoading(data.length)}
    </NBVStack>
  );
};
